import React from 'react';
import { useParams } from 'react-router-dom';
import { Play, Pause, Edit, Trash2, Mic2, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { shallow } from 'zustand/shallow';
import * as Sentry from '@sentry/react';

import { EditCustomProfileModal, PageParams } from './ConfigureVoiceModal';
import Spinner from '../Spinner';
import { usePreGenAudioPlayer } from '../../zustand-store/usePreGenAudioPlayerStore';
import { useSavedProfiles } from '../../http-hooks/saved-profiles/useSavedProfiles';
import { useDeleteProfile } from '../../http-hooks/ai-voices/useDeleteProfile';
import { keyable, useSavedProfilesStore } from '../../zustand-store/useSavedProfilesStore';
import { useAIVoicesStore } from '../../zustand-store/useAIVoicesStore';
import { useLanguages } from '../../http-hooks/ai-voices/useLanguages';
import { useVoiceConfigurationStore, VoiceConfigurationOptions } from '../../zustand-store/useVoiceConfigurationStore';
import { ChangeVoiceModal } from './ChangeVoiceModal';
import { C } from '../../styles/tokens';

interface SavedProfilesTabProps {
    org_id: string;
    integration_id: string;
    searchQuery?: string;
}

const glassCard: React.CSSProperties = {
    padding: '14px 16px',
    borderRadius: 12,
    border: `1px solid ${C.n200}`,
    background: C.w,
    transition: 'all 0.2s ease',
};

export const SavedProfilesTab: React.FC<SavedProfilesTabProps> = ({ org_id, integration_id, searchQuery = '' }) => {
    const setLanguages = useAIVoicesStore(state => state.setLanguages);
    const languages = useLanguages((data) => setLanguages(data));
    const { isLoading, isError, data } = useSavedProfiles({ org_id, id: integration_id });

    if (isLoading || languages.isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <Spinner color="var(--bs-primary)" />
            </div>
        );
    }

    if (isError) {
        return (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <p style={{ fontSize: 14, color: C.n400 }}>Failed to load saved profiles</p>
            </div>
        );
    }

    const profiles = data?.data || [];

    const filteredProfiles = profiles.filter((profile: any) => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
            profile.profile_name?.toLowerCase().includes(query) ||
            profile.lang_code?.toLowerCase().includes(query)
        );
    });

    if (filteredProfiles.length === 0) {
        if (searchQuery.trim()) {
            return (
                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                    <p style={{ fontSize: 14, color: C.n400 }}>No profiles found matching "{searchQuery}"</p>
                </div>
            );
        }

        return (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <div style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    background: C.p50,
                    border: `1px solid ${C.p100}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                }}>
                    <Mic2 size={28} color={C.p600} />
                </div>
                <h5 style={{ fontSize: 16, fontWeight: 600, color: C.n900, marginBottom: 8 }}>No saved profiles yet</h5>
                <p style={{ fontSize: 13, color: C.n400, marginBottom: 0 }}>
                    Create voice profiles to save your preferred voice settings
                </p>
            </div>
        );
    }

    return (
        <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px,1fr))', gap: 16 }}>
                {filteredProfiles.map((profile: any) => (
                    <SavedProfileCard key={profile.id} profile={profile} />
                ))}
            </div>
            <EditCustomProfileModal />
            <ChangeVoiceModal />
        </>
    );
};

interface SavedProfileCardProps {
    profile: keyable;
}

const SavedProfileCard: React.FC<SavedProfileCardProps> = ({ profile }) => {
    const { org_id, id } = useParams<PageParams>();
    const { setSelectedProfile, toggleEditProfile } = useSavedProfilesStore((state) => ({
        setSelectedProfile: state.setSelectedProfile,
        toggleEditProfile: state.toggleEditProfile,
    }), shallow);
    const setVoiceConfiguration = useVoiceConfigurationStore((state) => state.setVoiceConfiguration);
    const { mutateAsync: deleteProfile, isPending: isDeletingProfile } = useDeleteProfile();

    const languages = useAIVoicesStore((state) => state.languages);
    const language = languages[profile.lang_code];
    const voice = language?.voices.find(item => item.code === profile.voice && item.engine === profile.engine);

    const instanceKey = `saved-profiles-tab:${profile.id}`;
    const { activeKey, isPlaying, loadingSrc, togglePlayPause } = usePreGenAudioPlayer();
    const active = isPlaying && activeKey === instanceKey;
    const isLoadingAudio = activeKey === instanceKey && !!loadingSrc;

    const getAudioURL = (audio: string) => `${process.env.REACT_APP_AUDIO_PREVIEW_URL}/${audio}`;

    const handleEditProfile = () => {
        setSelectedProfile(profile);
        toggleEditProfile();

        setVoiceConfiguration(VoiceConfigurationOptions.volume, parseInt(profile.volume));
        setVoiceConfiguration(VoiceConfigurationOptions.speed, parseInt(profile.speed));
        setVoiceConfiguration(VoiceConfigurationOptions.pitch, profile.pitch || 'default');
        setVoiceConfiguration(VoiceConfigurationOptions.style, profile.style || 'default');
    };

    const handleDeleteProfile = async () => {
        if (profile.profile_name === "Default") {
            toast.error("Cannot delete the default profile");
            return;
        }

        if (!window.confirm(`Are you sure you want to delete the profile "${profile.profile_name}"? This action cannot be undone.`)) return;

        try {
            await deleteProfile({ org_id, integration_id: id, id: profile.id });
            toast.success(`Profile "${profile.profile_name}" deleted successfully`);
        } catch (error) {
            Sentry.captureException(error as any);
            console.error('Error deleting profile:', error);
            toast.error('Failed to delete profile. Please try again.');
        }
    };

    const isDefault = profile.profile_name === "Default";

    return (
        <div
            style={glassCard}
            onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                (e.currentTarget as HTMLElement).style.borderColor = C.p200;
                (e.currentTarget as HTMLElement).style.boxShadow = `0 4px 16px ${C.p100}`;
                (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
                (e.currentTarget as HTMLElement).style.borderColor = C.n200;
                (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
            }}
        >
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                {/* Play button */}
                <button
                    onClick={() => voice?.audio && !isLoadingAudio && togglePlayPause(getAudioURL(voice.audio), instanceKey)}
                    disabled={!voice?.audio || isLoadingAudio}
                    style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: voice?.audio
                            ? `linear-gradient(135deg, ${C.p600} 0%, ${C.p700} 100%)`
                            : C.p50,
                        border: 'none',
                        boxShadow: voice?.audio ? `0 2px 8px ${C.p100}` : 'none',
                        cursor: voice?.audio && !isLoadingAudio ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s ease',
                        color: voice?.audio ? 'white' : C.n400,
                        flexShrink: 0,
                        padding: 0
                    }}
                    onMouseEnter={(e) => { if (voice?.audio) e.currentTarget.style.opacity = '0.85'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                >
                    {isLoadingAudio
                        ? <Loader2 size={15} className="vi-spin" />
                        : active
                            ? <Pause size={15} fill="currentColor" />
                            : <Play size={15} fill="currentColor" />
                    }
                </button>

                {/* Profile info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <h6 style={{ fontSize: 14, fontWeight: 600, color: C.n900, margin: '0 0 6px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {profile.profile_name}
                    </h6>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: C.p50, border: `1px solid ${C.p100}`, color: C.p600, fontWeight: 600 }}>
                            {language?.name || profile.lang_code}
                        </span>
                        <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: C.n50, border: `1px solid ${C.n100}`, color: C.n400, fontWeight: 500 }}>
                            {voice?.name || 'Unknown Voice'}
                        </span>
                    </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <button
                        onClick={handleEditProfile}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: C.n400,
                            background: 'transparent',
                            border: '1px solid transparent',
                            borderRadius: 8,
                            width: 30,
                            height: 30,
                            cursor: 'pointer',
                            transition: 'all 0.18s ease',
                            padding: 0
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = C.p50;
                            e.currentTarget.style.borderColor = C.p100;
                            e.currentTarget.style.color = C.p600;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.borderColor = 'transparent';
                            e.currentTarget.style.color = C.n400;
                        }}
                    >
                        <Edit size={13} />
                    </button>

                    <button
                        onClick={handleDeleteProfile}
                        disabled={isDeletingProfile || isDefault}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: isDefault ? C.n300 : C.n400,
                            background: 'transparent',
                            border: '1px solid transparent',
                            borderRadius: 8,
                            width: 30,
                            height: 30,
                            cursor: isDefault ? 'not-allowed' : 'pointer',
                            transition: 'all 0.18s ease',
                            opacity: isDeletingProfile ? 0.5 : 1,
                            padding: 0
                        }}
                        onMouseEnter={(e) => {
                            if (!isDefault) {
                                e.currentTarget.style.background = C.r50;
                                e.currentTarget.style.borderColor = C.r100;
                                e.currentTarget.style.color = C.r500;
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isDefault) {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.borderColor = 'transparent';
                                e.currentTarget.style.color = C.n400;
                            }
                        }}
                        title={isDefault ? "Cannot delete default profile" : "Delete Profile"}
                    >
                        <Trash2 size={13} />
                    </button>
                </div>
            </div>
        </div>
    );
};
